/**
 * Renumber games — one division at a time, never automatic.
 *
 * A division has one sequence covering every game in it: pool, crossover,
 * bracket and consolation together, so a number names exactly one game all
 * weekend. Numbers are stable by design — moving a game's court or time does
 * not change it, and a deleted game leaves a permanent gap. This is the only
 * thing that closes those gaps, so it always asks first, and on a published
 * schedule it says exactly how many numbers coaches already hold will change.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Hash, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';

interface Props {
  eventId: string;
  division: string;
  disabled?: boolean;
  onDone?: () => void;
}

export default function RenumberGamesButton({ eventId, division, disabled, onDone }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ total: number; changed: number; published: boolean } | null>(null);

  const call = async (payload: Record<string, unknown>) =>
    invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'renumber_games', event_id: eventId, division, ...payload },
    });

  const openDialog = async () => {
    setBusy(true);
    setPreview(null);
    setOpen(true);
    try {
      const res: any = await call({ preview: true });
      if (!res?.ok) throw new Error(res?.error?.message ?? 'Could not read the current numbers.');
      setPreview({
        total: Number(res.data?.total ?? 0),
        changed: Number(res.data?.changed ?? 0),
        published: !!res.data?.published,
      });
    } catch (e: any) {
      setOpen(false);
      toast.error(e?.message ?? 'Could not read the current numbers.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const res: any = await call({ confirm: true });
      if (!res?.ok) throw new Error(res?.error?.message ?? 'Renumbering failed.');
      const changed = Number(res.data?.changed ?? 0);
      toast.success(
        changed === 0
          ? 'Nothing to renumber — the numbers were already in order.'
          : `${changed} game${changed === 1 ? '' : 's'} renumbered in ${division}.`,
      );
      setOpen(false);
      onDone?.();
    } catch (e: any) {
      toast.error(e?.message ?? 'Renumbering failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" disabled={disabled || busy} onClick={openDialog}>
        <Hash className="h-3.5 w-3.5 mr-1" /> Renumber {division}
      </Button>

      <AlertDialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Renumber every game in {division}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {!preview ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Checking what would change…
                  </div>
                ) : preview.changed === 0 ? (
                  <p>Nothing to renumber — all {preview.total} games are already in order.</p>
                ) : (
                  <>
                    <p>
                      {division} gets one run of numbers from 1 to {preview.total}: pool games first,
                      then crossovers, bracket and consolation, in date and time order. After this,
                      a number names exactly one game.{' '}
                      <strong>{preview.changed}</strong> of {preview.total} games change number.
                    </p>
                    {preview.published && (
                      <p className="rounded border border-amber-300 bg-amber-50 p-2 text-amber-900">
                        This schedule is published. Those are numbers coaches, scorekeepers and
                        printed sheets already have. Anyone holding an old sheet will be on the
                        wrong game number until you republish.
                      </p>
                    )}
                    <p className="text-muted-foreground">
                      Only {division} is affected. No other division moves.
                    </p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy || !preview || preview.changed === 0}
              onClick={(e) => { e.preventDefault(); void confirm(); }}
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Renumber {preview?.changed ?? 0} game{preview?.changed === 1 ? '' : 's'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
